/**
 * Text Behavior
 *
 * Renders text in 3D space using MSDF (Multi-channel Signed Distance Field) rendering.
 * Implements the Text RC spec — see scenedata/behaviors/textdata.ts for the
 * normative spec, units, and conformance requirements.
 */

import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Matrix } from '@babylonjs/core/Maths/math.vector';
import { Observer } from '@babylonjs/core/Misc/observable';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { FontAsset } from '@babylonjs/addons/msdfText/fontAsset';
import { TextRenderer } from '@babylonjs/addons/msdfText/textRenderer';
import { TraceBehavior } from './tracebehavior';
import { HEX_COLOR_RE, drivesValue, drivingHexColor } from './bindingValues';
import { TextData } from '../scenedata/behaviors/textdata';
import type { SceneObject } from '../core/SceneObject';
import { ValidationError } from '../scenedata/behaviordata';
import { SELECTION_MESH_SUFFIX } from './boxcollider';
import { Logger } from '../utils/Logger';
import { resolveFontAssets } from './text/fontRegistry';
import { ensureFontFamily, isFontFamilyLoading, onFontRegistryChange } from './text/hostedFonts';
import { registerTextCaptureSource, unregisterTextCaptureSource } from './text/textCaptureRegistry';
import { invalidateNearestLayout } from './layout';
import type { FontFamily, FontWeight, FontStyle } from '../scenedata/behaviors/textdata';
import {
    type FontMetrics,
    fontSizeToWorldScale,
    letterSpacingToFontUnits,
    lineHeightEmToRendererMultiplier,
    strokeWidthToSigDist,
    anchorXFraction,
    computeAnchorYOffset,
} from './text/units';
import {
    computeTextLayout,
    computeLineBoxExtents,
    toEditLayout,
    type LayoutFontApi,
    type LayoutFontChar,
    type TextLayoutParams,
    type TextEditLayout,
} from './text/textlayout';
import { resolveFontMetrics, type FaceMetrics } from './text/fontMetricsRegistry';
import { capTopFontUnits, type BmFontJson } from './text/bmfont';
import {
    layOutTextBox, measureTextBox, textMeasureCacheKey, wrapWidthToFontUnits,
    wrapEpsilonFontUnits, type MeasuredTextBox, type TextMeasureInput,
} from './text/measure';
import { TextBody, type BuiltTextBody, type TextBodyInput } from './text/textBody';
import { bakedFromRig } from '../loaders/restoreBakedSrc';

// Babylon's BMFont internal shape (subset we use).
interface BmFontInternal extends BmFontJson {
    distanceField?: { distanceRange: number };
}

const DEFAULT_FAMILY: FontFamily = 'Roboto';
const DEFAULT_WEIGHT: FontWeight = 'regular';
const DEFAULT_STYLE: FontStyle = 'normal';

/** Most the body's front sits behind the glyph plane, metres (the gap is `min(this, 2% of the em)`). */
const BODY_INSET_M = 0.0005;

/**
 * Coerce a resolved data field to a finite number, falling back when it
 * isn't one. Property references that fail to resolve leave the raw
 * `"$name"` string in `_resolved`, and bound properties can hold
 * non-numeric values — without this guard those flow into the world
 * matrix as NaN and render the text invisible.
 */
function finiteOr(value: unknown, fallback: number): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : fallback;
}

/**
 * The whole-text opacity, clamped to [0, 1]; 1 when absent. Clamped because an
 * eased bound opacity can overshoot mid-flight. Not `finiteOr`: that turns
 * `''` and `null` into 0, which would hide the text, where an absent,
 * non-numeric or unresolved (`"{name}"`) value must leave it as authored.
 */
function textOpacity(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;
}

/** Resolved fields that require a paragraph rebuild (vs. a renderer-property update). */
const PARAGRAPH_FIELDS = ['text', 'fontSize', 'anchorX', 'anchorY', 'lineHeight', 'letterSpacing', 'wrap'] as const;

/**
 * Every key required — an optional one lets a new PARAGRAPH_FIELDS entry be
 * left out of the record below and silently compare unequal forever, which
 * rebuilds the paragraph on every commit.
 */
type ParagraphState = { [K in (typeof PARAGRAPH_FIELDS)[number]]: TextData[K] };

/**
 * Renders text in 3D world space using MSDF technique.
 */
export class Text extends TraceBehavior<TextData> {
    static readonly behaviorType = "Text" as const;

    private fontAsset: FontAsset | null = null;
    private textRenderer: TextRenderer | null = null;
    /** The alpha last given the stroke colour, before opacity; 1 on a new renderer. */
    private _strokeAlpha = 1;
    private renderObserver: Observer<Camera> | null = null;
    private _selectionMesh: Mesh | null = null;

    // Cached font metrics; replaced on each font load. Initialised to Roboto
    // fallbacks so geometry queries before load() return sensible numbers.
    private _metrics: FontMetrics = {
        emSize: 80,
        distanceRange: 4,
        naturalLineHeightEm: 1.325, // Roboto Regular: 106 / 80
        capTop: 27, // Roboto Regular: 'H' yoffset 25 + padding 2
    };

    private _loadedFontKey: string | undefined;

    // The face this paragraph was last measured or loaded in, by identity.
    // A face landing for the family only matters if it changes this. A load
    // sets it when it starts, so after a failed load it names the face asked
    // for while the previous one still draws, and that face is not retried
    // when it registers: the next commit or a different face's arrival
    // reloads.
    private _drawnFace: FaceMetrics | undefined;

    // Paragraph-relevant resolved fields as last applied to the renderer
    // (recorded by updateTextContent). applyCommit diffs against this —
    // NOT against authored data — to decide whether a rebuild is needed.
    // Comparing resolved-vs-authored breaks once a field holds a property
    // reference: the authored "$name" never equals the resolved value, so
    // every commit would take the rebuild branch.
    private _appliedParagraph: ParagraphState | null = null;

    // Monotonic counter to detect stale createTextRenderer() runs when
    // load() is invoked again before one finishes (e.g. font property changed
    // twice in quick succession via applyCommit). Each load() takes the next
    // generation when it is queued, and its run bails before writing to
    // `this.*` once a newer load() or a detach has moved the counter past it.
    private _loadGeneration = 0;

    // load() calls queued or running. A paragraph is not settled in its face
    // while this is non-zero (see isFontSettled).
    private _pendingLoads = 0;

    // Host-driven presentation fade, 1 = off. Multiplies the drawn alpha only;
    // it is not part of TextData, never saved, exported or undone.
    private _hostFade = 1;

    // Last measurement published to layout (line-box basis), cached for
    // change detection only — reads always measure fresh through the
    // metrics registry, which works with or without a loaded font.
    private _layoutMeasure: {
        width: number; height: number; centerX: number; centerY: number; layoutCenterX: number; thickness: number;
    } | null = null;

    // The layout-resolved width this paragraph wraps to, world units. Null
    // when the node is not layout-managed, its width axis is Hug, or it has
    // left layout management — all of which mean "no constraint", so the
    // paragraph breaks only at authored newlines. Written by the layout
    // bridge (setLayoutResolvedWidth) after resolve.
    private _layoutWrapWidth: number | null = null;
    private _layoutSlotWidth: number | null = null;

    // Last measurement, keyed by everything it depends on. getLayoutContentSize
    // is called once per node per solve pass (twice with the centre hook), and
    // wrapping makes a measure O(text length) — without this the editor pays
    // for a full re-layout on every pass of every keystroke.
    private _measureCache: { key: string; box: MeasuredTextBox } | null = null;

    /**
     * A hosted face this paragraph was waiting on has arrived.
     *
     * Measurement is synchronous and never blocks on a fetch, so a paragraph
     * naming a not-yet-loaded family is laid out immediately through the
     * fallback chain — correct behaviour, and the reason a page draws at all
     * before the network answers. What must NOT happen is that it stays that
     * way: the cached box was measured against the fallback face, and the
     * layout around it was solved from that box.
     *
     * So: drop the cache, re-measure, and let `_publishMeasurement` re-solve
     * the nearest layout if the number actually moved (it won't, if the new
     * face happens to measure the same — a hosted Roboto clone re-solves
     * nothing). Then reload the renderer so the glyphs follow the metrics.
     *
     * A pack lands one face at a time and a paragraph draws in one cut of it,
     * so this only acts when the arrival changes the face THIS paragraph
     * resolves to. A five-weight pack costs a paragraph one reload, not five.
     *
     * Bound as a field so the subscription can be removed on detach.
     */
    private _onFontRegistered = (family: string): void => {
        if (this._detached) return;
        const data = this._resolved;
        const want = drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY;
        if (want !== family) return;
        if (this._currentFace() === this._drawnFace) return;

        this._measureCache = null;
        this._publishMeasurement();

        // Only reload if a load has ever been attempted. A behavior that has
        // not been asked to render must not start rendering because a font
        // showed up; its first load() will resolve the new face anyway. An
        // in-flight load that already resolved the fallback is superseded by
        // the generation bump this load() makes.
        if (this._loadGeneration > 0 || this.textRenderer) {
            void this.load();
        }
    };

    private _fontRegistryUnsubscribe: (() => void) | null = null;

    // The extruded body, created the first time `depth` asks for one, and the
    // input it was last built from (a refresh with the same input is free).
    // A build that failed is retried by the next load, not by every commit:
    // an opacity transition commits each frame.
    private _body: TextBody | null = null;
    private _bodyKey: string | null = null;
    private _bodyFailed = false;

    constructor(sceneObject: SceneObject, data: TextData) {
        super(sceneObject, data);
        this._loadedFontKey = currentFontKey(data);
        this._listenForFonts();
    }

    /**
     * Listen for hosted faces from construction, not from attach: a scene loads
     * every behavior before it attaches any, so a pack that lands in between
     * would otherwise be missed and the paragraph would open in the fallback.
     *
     * The registry is page-wide, so its listener set outlives every scene. A
     * paragraph that is never attached never reaches `onDetach`, so the
     * subscription also ends when the node it belongs to is disposed (which a
     * scene disposal does for every node). Whichever comes first wins.
     */
    private _listenForFonts(): void {
        const node = this.sceneObject.transform;
        if (node.isDisposed()) return;
        const stop = onFontRegistryChange(this._onFontRegistered);
        const observer = node.onDisposeObservable.add(() => this._stopListeningForFonts());
        this._fontRegistryUnsubscribe = () => {
            stop();
            node.onDisposeObservable.remove(observer);
        };
    }

    private _stopListeningForFonts(): void {
        this._fontRegistryUnsubscribe?.();
        this._fontRegistryUnsubscribe = null;
    }

    // ============================================================================
    // LIFECYCLE
    // ============================================================================

    // Serialize loads: concurrent load() calls (font-property commits racing
    // initial load) each created a TextRenderer, and the orphan kept drawing —
    // the same text rendered twice at slightly different metrics.
    private _loadChain: Promise<void> = Promise.resolve();
    private _detached = false;

    async load(): Promise<void> {
        this._pendingLoads++;
        // A newer load supersedes one in flight or queued: its fetch or atlas
        // wait ends at the next stale check, and only the newest face is installed.
        const generation = ++this._loadGeneration;
        this._loadChain = this._loadChain
            // Self-heal: a rejected link (e.g. resolveFontAssets throwing
            // before createTextRenderer's own try/catch) must not poison the
            // chain — otherwise every later load() re-propagates the old
            // rejection and the behavior never updates again.
            .catch((error) => {
                Logger.warn(`[Text] previous load failed: ${String(error)}`);
            })
            .then(async () => {
                try {
                    // The renderer drawing now is NOT disposed here: it keeps
                    // drawing until the replacement is built and its atlas has
                    // arrived, and createTextRenderer swaps them in one step.
                    // Disposing first left the paragraph blank for the whole
                    // definition fetch + atlas download of the new face.
                    await this.createTextRenderer(generation);
                } finally {
                    this._pendingLoads--;
                }
                // The body needs no renderer: a face whose atlas failed still extrudes.
                if (this._bodyFailed) this._bodyKey = null;
                void this._refreshBody();
            });
        return this._loadChain;
    }

    /**
     * True when nothing more is coming for this paragraph's face: no load
     * queued or running, the family's hosted pack is not still on the wire,
     * and the last load was for the face the data resolves to now. A host
     * uses it to know when a style or font change has finished arriving. A
     * load that failed counts as settled even though the previous face is
     * still drawn: nothing more is coming.
     */
    public isFontSettled(): boolean {
        if (this._detached) return true;
        if (this._pendingLoads > 0) return false;
        const data = this._resolved;
        const family = drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY;
        if (isFontFamilyLoading(family)) return false;
        if (this._loadGeneration === 0 && !this.textRenderer) return true;
        return this._currentFace() === this._drawnFace;
    }

    /**
     * Fade the drawn glyphs by `alpha` (0..1; 1 clears it) without touching the
     * data. For a host to show that a change is on its way and ease it in: a
     * presentation of the live renderer only, so it is not saved, exported or
     * recorded for undo, and it survives a face swap.
     */
    public setHostFade(alpha: number): void {
        const next = Number.isFinite(alpha) ? Math.min(1, Math.max(0, alpha)) : 1;
        if (next === this._hostFade) return;
        this._hostFade = next;
        if (this.textRenderer) this.applyTextProperties();
    }

    init(): void {
        // Properties are applied during createTextRenderer / addParagraph.
    }

    protected onAttach(_target: TransformNode): void {
        // A provider ARRIVING changes the solve exactly as one leaving does
        // (see onDetach, same edge). Without this a Text added to an existing
        // node never re-solves its container, and standalone it never picks up
        // the width its own LayoutItem already carries — attach order decided
        // whether a paragraph wrapped (#709).
        invalidateNearestLayout(this.sceneObject);
        const parent = this._getParentNode();
        if (this.textRenderer) {
            this.textRenderer.parent = parent;
        }
        this._body?.setParent(parent);
        // Every load runs load() before attach, so the paragraph was laid out
        // with no node to hang the pick box from — place it now.
        this._placeSelectionMesh();
    }

    protected onDetach(): void {
        this._detached = true;
        this._stopListeningForFonts();
        this.disposeTextRenderer();
        this._body?.dispose();
        this._body = null;
        this._bodyKey = null;
        // Removed glyphs leave the container hugging a ghost measurement —
        // re-solve without this provider (same edge as Model.onDetach).
        invalidateNearestLayout(this.sceneObject);
    }

    onAnimationRootCreated(animationRoot: TransformNode): void {
        if (this.textRenderer) {
            this.textRenderer.parent = animationRoot;
        }
        if (this._selectionMesh) {
            this._selectionMesh.parent = animationRoot;
        }
        this._body?.setParent(animationRoot);
    }

    // ============================================================================
    // TEXT RENDERING
    // ============================================================================

    private async createTextRenderer(generation: number): Promise<void> {
        // Stale when a newer load superseded us — or when the behavior was
        // detached while our fetches were in flight. Without the detach check,
        // deleting a scene mid-load left a fully-live orphan renderer drawing
        // its paragraph into every subsequent scene (sequential bench captures
        // showed the previous scene's title ghosted under the new one).
        const isStale = (): boolean => generation !== this._loadGeneration || this._detached;
        // A load superseded while it was still queued does nothing at all.
        if (isStale()) return;

        const data = this._resolved;
        // An unresolved `{titleFont}` is not a family name. The registry would
        // fall back to the default anyway, but only after warning once per
        // distinct key — noise on every themed text node outside its provider.
        const family = drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY;
        const weight = data.fontWeight ?? DEFAULT_WEIGHT;
        const style = data.fontStyle ?? DEFAULT_STYLE;
        // Same trigger as _resolveFace, for the node that renders without ever
        // being measured. Idempotent, so the common path pays a Set lookup.
        void ensureFontFamily(family);
        const { definitionUrl, textureUrl } = resolveFontAssets(family, weight, style);
        this._drawnFace = resolveFontMetrics(family, weight, style);

        try {
            const response = await fetch(definitionUrl);
            if (isStale()) return;
            if (!response.ok) {
                throw new Error(`Failed to fetch font definition: ${response.status}`);
            }
            const fontDefinition = await response.text();
            if (isStale()) return;

            const fontAsset = new FontAsset(fontDefinition, textureUrl, this.scene);

            const engine = this.scene.getEngine();
            const textRenderer = await TextRenderer.CreateTextRendererAsync(fontAsset, engine);
            // Only a paragraph already on screen has a drawing worth holding; a first load installs at once.
            if (this.textRenderer && !isStale()) await this._whenAtlasReady(fontAsset, isStale);
            if (isStale()) {
                // A newer load() has started since we kicked off; throw away
                // our products instead of clobbering the newer renderer.
                textRenderer.dispose();
                fontAsset.dispose();
                return;
            }

            // Swap, with no await between the old renderer going and the new
            // one standing, so no frame draws without the paragraph.
            this._releaseRenderer();
            this.fontAsset = fontAsset;
            this.textRenderer = textRenderer;
            this._strokeAlpha = 1;
            this._extractFontMetrics();
            this.applyTextProperties();

            const parent = this._getParentNode();
            if (parent) {
                this.textRenderer.parent = parent;
            }

            this.updateTextContent();

            this.renderObserver = this.scene.onAfterCameraRenderObservable.add((cam) => {
                this.renderGlyphs(cam.getViewMatrix(), cam.getProjectionMatrix());
            });

            // Expose the live renderer to RTT capture passes (thumbnails,
            // publish previews) — RTT renders never fire the camera-render
            // observable above, so without this the glyphs exist only on the
            // live canvas and every captured thumbnail loses its text. The
            // matching unregister is in _releaseRenderer, run on detach and
            // in the swap above just before this, so captures never see a
            // disposed renderer.
            registerTextCaptureSource(this.scene, this);

            Logger.debug(`[Text] Created text renderer for ${this.sceneObject.name}`);
        } catch (error) {
            Logger.warn(`[Text] Failed to create text renderer: ${String(error)}`);
        }
    }

    /**
     * Cache em-relative metrics from the loaded font. Em-square size and
     * MSDF distance range come straight from the BMFont JSON; natural
     * line-height is the font's `common.lineHeight` divided by em-square;
     * the cap line is `capTopFontUnits` of the same JSON. Once per font load.
     */
    private _extractFontMetrics(): void {
        if (!this.fontAsset) return;
        const font = (this.fontAsset as unknown as { _font: BmFontInternal })._font;
        const emSize = font.info.size;

        this._metrics = {
            emSize,
            distanceRange: font.distanceField?.distanceRange ?? 4,
            naturalLineHeightEm: font.common?.lineHeight && emSize > 0
                ? font.common.lineHeight / emSize
                : this._metrics.naturalLineHeightEm,
            // From the atlas being drawn, by the same rule the registry ran
            // over the same JSON, so the cap line matches these glyphs.
            capTop: capTopFontUnits(font),
        };
    }

    /**
     * Read the font's intrinsic `common.lineHeight` in raw font units, used
     * to convert spec lineHeight (em-relative) → renderer lineHeight
     * (relative to common.lineHeight). Falls back to `emSize` when the
     * font's metric is missing or the font isn't loaded yet.
     */
    private _getCommonLineHeightFontUnits(): number {
        const font = this.fontAsset
            ? (this.fontAsset as unknown as { _font: BmFontInternal })._font
            : undefined;
        return font?.common?.lineHeight ?? this._metrics.emSize;
    }

    /**
     * The fill colour as drawn, opacity applied: the glyphs' colour and the
     * body's.
     *
     * A colour that doesn't drive — an unresolved binding, `''`, or a
     * malformed hex — falls back to white rather than reaching
     * Color4.FromHexString, which answers TRANSPARENT BLACK for junk and
     * would make the text silently disappear rather than look wrong. Every
     * generated text node carries a bound colour, so this is the ordinary
     * path whenever a node sits outside the subtree its theme declares.
     *
     * Opacity multiplies the colour's own alpha (the spec's rule), here and
     * on the stroke. It is applied with the colours, so a per-frame state
     * transition never reaches the paragraph rebuild.
     */
    private _fillColor(): Color4 {
        const data = this._resolved;
        const colorHex = drivingHexColor(data.color, 'Text.color') ?? '#FFFFFF';
        const color4 = Color4.FromHexString(colorHex.length === 7 ? colorHex + 'FF' : colorHex);
        color4.a *= textOpacity(data.opacity);
        return color4;
    }

    private applyTextProperties(): void {
        if (!this.textRenderer) return;
        const data = this._resolved;

        // The host fade dims the glyph renderer only: the body's colour is what
        // the export bakes, so a fade in progress must not reach it.
        const opacity = textOpacity(data.opacity) * this._hostFade;
        const color4 = this._fillColor();
        color4.a *= this._hostFade;
        this.textRenderer.color = color4;
        this._body?.setColor(this._bodyColor());

        // Stroke. Spec: strokeWidth is a fraction of cap-height (0..0.2).
        // Babylon's strokeOutsetWidth is added directly to the MSDF signed-distance
        // field. The shader's edge transition spans 1.0 sigDist unit across the
        // distanceRange-pixel padding around each glyph. So a stroke that is
        // visually `f` cap-heights wide must equal `f * capHeightEm` em, which
        // (in atlas pixels) is `f * capHeightEm * info.size`, normalised to
        // sigDist by dividing by `distanceRange / 2` (half because the MSDF
        // distance field is signed about the edge).
        const strokeHex = drivingHexColor(data.strokeColor, 'Text.strokeColor');
        if (strokeHex && data.strokeWidth && data.strokeWidth > 0) {
            const strokeColor4 = Color4.FromHexString(strokeHex.length === 7 ? strokeHex + 'FF' : strokeHex);
            this._strokeAlpha = strokeColor4.a;
            strokeColor4.a *= opacity;
            this.textRenderer.strokeColor = strokeColor4;
            this.textRenderer.strokeOutsetWidth = strokeWidthToSigDist(data.strokeWidth, this._metrics);
        } else {
            this.textRenderer.strokeOutsetWidth = 0;
            // The shader mixes the stroke colour in at antialiased glyph edges
            // even with no outline, so its alpha fades too or opacity 0 leaves
            // a ghost. The colour stays as it was; multiplying the kept alpha
            // (not the renderer's current one) keeps a translucent stroke
            // translucent and a change of opacity from compounding.
            this.textRenderer.strokeColor.a = this._strokeAlpha * opacity;
        }
    }

    private updateTextContent(): void {
        if (!this.textRenderer || !this.fontAsset) return;
        const data = this._resolved;

        // Authored data renders VERBATIM — escape normalization for
        // tool-authored strings happens at the authoring boundary
        // (editor/authoredText.ts), never here.
        const rawText = data.text ?? 'Text';
        const text = typeof rawText === 'string' ? rawText : String(rawText);
        const { scale, anchorY, rendererLineHeight, layoutParams } = this._deriveParagraphParams();

        // Idempotence: clear any previously-added paragraph first. Several
        // paths (font load, commit, layout re-measure) can land here twice —
        // without the reset the old glyphs stay and render as a ghost/double
        // strike under the new paragraph.
        {
            const renderer = this.textRenderer as unknown as {
                _charMatrices: number[]; _charUvs: number[]; _isDirty: boolean;
            };
            if (renderer._charMatrices?.length) {
                renderer._charMatrices.length = 0;
                renderer._charUvs.length = 0;
                renderer._isDirty = true;
            }
        }

        const layout = computeTextLayout(text, layoutParams, this._fontApi());

        // The layout above IS the paragraph: hand it to Babylon through the
        // customLayoutEngine hook so rendering, measurement, and editor
        // caret math all share one set of glyph positions. (Babylon's
        // built-in engine collapses space runs and trims line ends, and
        // knows nothing of our wrap; ours is the Text spec's.)
        const paragraphOptions: Record<string, unknown> = {
            lineHeight: rendererLineHeight,
            letterSpacing: layoutParams.letterSpacingUnits,
            customLayoutEngine: () => ({
                paragraph: layout.text,
                lines: layout.lines,
                glyphs: layout.glyphs,
                width: layout.width,
                height: layout.height,
            }),
        };

        // Anchor to the LINE BOX (lineCount × lineHeight × em), not the ink
        // span — the box the spec means by "the rendered text box", the box
        // layout measures, and the box the editor shows.
        const box = computeLineBoxExtents(layout, anchorY, this._metrics.capTop);
        const fontScale = 1 / this._metrics.emSize;
        const boxTopEm = box.yTop * fontScale;
        const boxBotEm = box.yBot * fontScale;

        const yWorldOffset = computeAnchorYOffset(anchorY, boxTopEm, boxBotEm, scale);

        // Babylon row-vector convention: `v × A.multiply(B)` = `v × A × B`,
        // applied left-to-right. We want the translation to be a world-space
        // offset (already pre-scaled by `computeAnchorYOffset`), so scale
        // first, then translate.
        const worldMatrix = Matrix.Scaling(scale, scale, scale)
            .multiply(Matrix.Translation(0, yWorldOffset, 0));

        this.textRenderer.addParagraph(text, paragraphOptions, worldMatrix);

        // Record the resolved paragraph state only after a successful
        // addParagraph, so a future commit that diffs against it can't skip a
        // rebuild that never actually landed.
        this._appliedParagraph = {
            text: data.text,
            fontSize: data.fontSize,
            anchorX: data.anchorX,
            anchorY: data.anchorY,
            lineHeight: data.lineHeight,
            letterSpacing: data.letterSpacing,
            wrap: data.wrap,
        };

        this._placeSelectionMesh();

        // Publish the measurement for layout (Hug sizing + slot centering) —
        // a text/style commit routed through a rebuild is what re-solves the
        // arrangement around the new paragraph.
        this._publishMeasurement();

        void this._refreshBody();
    }

    /**
     * The measurement metrics for the face this paragraph resolves to. The
     * generated registry, not the fetched atlas — so the answer is the same
     * headless and in the browser, and available before the font loads. It
     * mirrors the same bundled BMFont the renderer ends up with (identical
     * em-square included), which is what lets the render path below scale a
     * registry-derived font-unit number by `this._metrics.emSize`.
     */
    private _resolveFace(): FaceMetrics {
        const data = this._resolved;
        const family = drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY;
        // A family this deployment publishes but has not fetched yet starts
        // arriving here — the first place anything asks to be drawn in it.
        // Idempotent and non-blocking: this measure still resolves through the
        // fallback chain, and `_onFontRegistered` re-measures when the real
        // face lands.
        void ensureFontFamily(family);
        this._drawnFace = this._currentFace();
        return this._drawnFace;
    }

    /** The face this paragraph resolves to right now, without asking for it. */
    private _currentFace(): FaceMetrics {
        const data = this._resolved;
        return resolveFontMetrics(
            drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY,
            data.fontWeight ?? DEFAULT_WEIGHT,
            data.fontStyle ?? DEFAULT_STYLE,
        );
    }

    /**
     * Measure the paragraph's line box (world units) through the generated
     * metrics registry — NOT the fetched font asset — so the numbers are
     * identical in the browser and headless, and identical before and after
     * the atlas loads. The registry mirrors the bundled BMFont JSONs
     * verbatim and resolves through the same fallback chain as the
     * renderer, so measured face == rendered face.
     */
    private _measureContentBox(wrapWidthOverride?: number | null): MeasuredTextBox {
        // `_resolveFace` first: it is what kicks off a hosted-font fetch. The
        // arithmetic itself lives in `text/measure.ts`, shared verbatim with
        // the headless callers, so what the solver hugs here and what a tool
        // measures before the scene exists cannot drift apart.
        const face = this._resolveFace();
        const input = this._measureInput(wrapWidthOverride);

        const key = textMeasureCacheKey(input);
        const cached = this._measureCache;
        if (cached && cached.key === key) return cached.box;

        const box = measureTextBox(input, face);
        this._measureCache = { key, box };
        return box;
    }

    /** The fields measurement and the body lay the paragraph out from. */
    private _measureInput(wrapWidthOverride?: number | null): TextMeasureInput {
        const data = this._resolved;
        const rawText = data.text ?? 'Text';
        return {
            text: typeof rawText === 'string' ? rawText : String(rawText),
            fontSize: finiteOr(data.fontSize, 14),
            lineHeight: finiteOr(data.lineHeight, 1.2),
            letterSpacing: finiteOr(data.letterSpacing, 0),
            anchorX: data.anchorX ?? 'center',
            anchorY: data.anchorY ?? 'middle',
            fontFamily: drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY,
            fontWeight: data.fontWeight ?? DEFAULT_WEIGHT,
            fontStyle: data.fontStyle ?? DEFAULT_STYLE,
            wrap: data.wrap,
            wrapWidth: wrapWidthOverride !== undefined ? wrapWidthOverride : this._layoutWrapWidth,
            // An explicit override is both the wrap and the slot, as before.
            slotWidth: wrapWidthOverride !== undefined ? undefined : this._layoutSlotWidth,
        };
    }

    // ============================================================================
    // EXTRUDED BODY
    // ============================================================================

    /**
     * What the body is built from, or null when there is none to build: flat
     * text, a face without outlines (hosted), or a `src` that already is the
     * body (a player opening a baked file).
     *
     * Laid out by the same arithmetic as the measurement, against the registry
     * face, so the body sits under the drawn glyphs whether or not an atlas
     * ever loaded.
     */
    private _bodyInput(): { key: string; input: TextBodyInput | null } {
        const depth = this._depthEm();
        if (depth <= 0) return { key: '', input: null };
        const outlineUrl = this._outlineUrl();
        if (!outlineUrl || this._srcIsBody()) return { key: '', input: null };
        const measureInput = this._measureInput();
        const stroke = this._strokeEm();
        // Keyed on what layOutTextBox reads: the slot, and the wrap width of a
        // paragraph that doesn't wrap, move the box but not the glyphs.
        const layoutKey = textMeasureCacheKey({
            ...measureInput,
            slotWidth: undefined,
            wrapWidth: measureInput.wrap === false ? null : measureInput.wrapWidth,
        });
        const key = `${outlineUrl}|${depth}|${stroke}|${layoutKey}`;
        if (key === this._bodyKey) return { key, input: null };
        const face = this._currentFace();
        const { layout, scale, unitScale, yWorldOffset } = layOutTextBox(measureInput, face);
        return {
            key,
            input: {
                outlineUrl,
                glyphs: layout.glyphs.map(g => ({ code: g.char.id, x: g.x, y: g.y })),
                frame: { unitScale, yOffset: yWorldOffset, depth: depth * scale, inset: this._bodyInset(scale), outset: stroke * face.emSize },
            },
        };
    }

    /**
     * The stroke the face draws, em: what the body grows by. Capped where the
     * atlas stops drawing it wider (its distance field reaches half its range
     * past the edge), so the body's edge stays on the stroke's.
     */
    private _strokeEm(): number {
        const data = this._resolved;
        const width = finiteOr(data.strokeWidth, 0);
        if (width <= 0 || !this._strokeHex()) return 0;
        return Math.min(width, this._metrics.distanceRange / (2 * this._metrics.emSize));
    }

    /** The stroke colour if it drives, without a warning: applyTextProperties already logs a bad one. */
    private _strokeHex(): string | null {
        const value = this._resolved.strokeColor;
        return drivesValue(value) && HEX_COLOR_RE.test(value) ? value : null;
    }

    /** The body's colour: the stroke's when the face is stroked (its walls continue the outline), else the fill's. */
    private _bodyColor(): Color4 {
        const hex = this._strokeEm() > 0 ? this._strokeHex() : null;
        if (!hex) return this._fillColor();
        const color4 = Color4.FromHexString(hex.length === 7 ? hex + 'FF' : hex);
        color4.a *= textOpacity(this._resolved.opacity);
        return color4;
    }

    /** Resolved `depth`, em, clamped to its 0–1 range: a bound value is not validated on commit. */
    private _depthEm(): number {
        return Math.min(1, Math.max(0, finiteOr(this._resolved.depth, 0)));
    }

    /** Whether `src` is this Text's baked body: a player opened the file it was saved in. */
    private _srcIsBody(): boolean {
        const provenance = this.sceneObject.getBehavior('Provenance')?.getData();
        return bakedFromRig(this.sceneObject.src, provenance ? [provenance] : undefined) === '';
    }

    /** The resolved face's glyph outlines; none for a face that carries none (hosted). */
    private _outlineUrl(): string | undefined {
        const data = this._resolved;
        const family = drivesValue(data.fontFamily) ? data.fontFamily : DEFAULT_FAMILY;
        return resolveFontAssets(family, data.fontWeight ?? DEFAULT_WEIGHT, data.fontStyle ?? DEFAULT_STYLE).outlineUrl;
    }

    private _bodyInset(scale: number): number {
        return Math.min(BODY_INSET_M, 0.02 * scale);
    }

    /**
     * Bring the body up to date with the data; nothing is rebuilt while its
     * input is unchanged. A build that failed is retried when the input
     * changes or the Text loads again.
     */
    private _refreshBody(): Promise<void> {
        // A load or fetch that lands after detach must not raise a body nothing disposes.
        if (this._detached) return Promise.resolve();
        const { key, input } = this._bodyInput();
        this._body?.setColor(this._bodyColor());
        if (key === this._bodyKey) return Promise.resolve();
        this._bodyKey = key;
        if (!input && !this._body) return Promise.resolve();
        this._body ??= new TextBody(this.scene, this.sceneObject.name);
        this._body.setParent(this._getParentNode());
        this._body.setColor(this._bodyColor());
        this._bodyFailed = false;
        return this._body.update(input).then((ok) => {
            if (!ok && this._bodyKey === key) this._bodyFailed = true;
        });
    }

    /** Front of the glyphs to the back of the body, metres; 0 for flat text, and for a face that cannot extrude. */
    private _thickness(): number {
        const depth = this._depthEm();
        if (depth <= 0 || !this._outlineUrl()) return 0;
        const scale = fontSizeToWorldScale(finiteOr(this._resolved.fontSize, 14));
        return depth * scale + this._bodyInset(scale);
    }

    /** Whether the text has a body to draw: a depth, as resolved (a bound one included), in a face that can extrude. */
    public isExtruded(): boolean {
        return this._thickness() > 0;
    }

    /**
     * The extruded body as the export bakes it, built from the current data;
     * null for flat text, or when the body is already this node's `src`.
     */
    public async buildBody(): Promise<BuiltTextBody | null> {
        // One call, not a stream of commits: a build that failed is worth another try.
        if (this._bodyFailed) this._bodyKey = null;
        await this._refreshBody();
        return this._body ? this._body.built() : null;
    }

    /**
     * The wrap constraint in font units, or undefined when this paragraph
     * must not wrap: no layout-resolved width (standalone text, or a Hug
     * width with no maxWidth — nothing to wrap against), a degenerate
     * fontSize, or `wrap: false` (the buttons-and-badges opt-out).
     */
    private _maxWidthUnits(wrapWidth: number | null, emSize: number, scale: number): number | undefined {
        return wrapWidthToFontUnits(wrapWidth, emSize, scale, this._resolved.wrap);
    }

    /**
     * Slack for the "already fits" fast path, font units: one layout unit
     * (`LAYOUT_UNIT_M`, a millimetre) — absolute, not a relative tolerance.
     */
    private _wrapEpsilonUnits(emSize: number, scale: number): number {
        return wrapEpsilonFontUnits(emSize, scale);
    }

    /**
     * Re-measure at the current wrap state, cache, and re-solve the nearest
     * layout when the measurement meaningfully changed. Shared by the render
     * rebuild path and the headless receiver path.
     */
    private _publishMeasurement(): void {
        const m = { ...this._measureContentBox(), thickness: this._thickness() };
        const prev = this._layoutMeasure;
        this._layoutMeasure = m;
        const changed = !prev
            || Math.abs(prev.width - m.width) > 1e-6
            || Math.abs(prev.height - m.height) > 1e-6
            || Math.abs(prev.centerX - m.centerX) > 1e-6
            || Math.abs(prev.centerY - m.centerY) > 1e-6
            // The slot moved under an unchanged run: layout places the node by
            // this, so it must re-place it.
            || Math.abs(prev.layoutCenterX - m.layoutCenterX) > 1e-6
            // `depth` and a face that cannot extrude move only the thickness.
            || Math.abs(prev.thickness - m.thickness) > 1e-6;
        if (changed) invalidateNearestLayout(this.sceneObject);
    }

    /**
     * The line box and its centre are in the node's own frame, as if the node
     * were not turned: a node that hugs what it holds turns them.
     */
    readonly layoutContentFrame = 'node' as const;

    /** Paragraph line-box size (world units) — LayoutSizeProvider. */
    getLayoutContentSize(): { x: number; y: number; z: number } | null {
        const m = this._measureContentBox();
        return { x: m.width, y: m.height, z: this._thickness() };
    }

    /**
     * Line-box size the paragraph would report if it had to fit `width` —
     * the width-carrying half of the LayoutSizeProvider contract, i.e. how
     * text answers "how tall are you in a slot this wide?".
     *
     * Layout measures at the last resolved width, or the authored Fixed width
     * on the first pass. This is speculative: only `setLayoutResolvedWidth`
     * commits the paragraph's wrap state.
     */
    getLayoutContentSizeForWidth(width: number): { x: number; y: number; z: number } | null {
        const m = this._measureContentBox(width);
        return { x: m.width, y: m.height, z: this._thickness() };
    }

    /**
     * Take the layout-resolved width and re-lay the glyphs on the lines it
     * implies. Null means the node left layout management (or has no width
     * to wrap against) and the paragraph returns to authored breaks only.
     *
     * Republishing the new measurement is what makes a Hug-height ancestor
     * settle around wrapped text: the changed box invalidates the nearest
     * layout, and that stabilization pass measures with this width already
     * in hand. It runs at most once — the second pass resolves the same
     * width, which returns here early.
     */
    setLayoutResolvedWidth(width: number | null, slotWidth: number | null = width): void {
        const next = width != null && width > 0 ? width : null;
        const nextSlot = slotWidth != null && slotWidth > 0 ? slotWidth : null;
        if (next === this._layoutWrapWidth && nextSlot === this._layoutSlotWidth) return;
        this._layoutWrapWidth = next;
        this._layoutSlotWidth = nextSlot;
        void this._refreshBody();
        if (this._resolved.wrap === false) {
            // Wrapping is disabled, but the alignment slot can still move.
            this._publishMeasurement();
            return;
        }

        if (this.textRenderer && this.fontAsset) {
            // Rebuilds the paragraph AND publishes the measurement.
            this.updateTextContent();
        } else {
            this._publishMeasurement();
        }
    }

    // Export the solved wrap width for players without touching authored
    // layout. setLayoutResolvedWidth keeps any positive width, Infinity
    // included, so only a finite one is written.
    override serialize(): TextData {
        const data = super.serialize();
        delete data.bakedWrapWidth;
        if (this._resolved.wrap !== false && this._layoutWrapWidth != null
            && Number.isFinite(this._layoutWrapWidth)) {
            data.bakedWrapWidth = this._layoutWrapWidth;
        }
        return data;
    }

    /**
     * Visual centre of the paragraph's BOX relative to the node origin — lets
     * layout place anchored text (e.g. anchorX "left" draws glyphs rightward
     * of the origin) in its slot instead of positioning the anchor point.
     *
     * The box is the SLOT, not the glyph run, whenever layout imposed a slot
     * for this node; a ceiling alone keeps the natural run's centre.
     * Layout subtracts whatever comes back from the slot's
     * centre, so reporting the run's centre made the solver park the RUN's
     * midpoint on the slot's midpoint, which centres a left-anchored paragraph
     * inside its slot however wide the slot is. The lines stayed left-aligned
     * relative to each other, so the tell was a short line floating in the
     * middle of a wide box: a one-line meta row sitting centred across a wide
     * pill instead of hard against its left edge, and every card's meta line
     * drifting off its title.
     *
     * The rule `anchorX` states is about the SLOT: "left" puts the paragraph
     * at the slot's left edge. At "left" the origin lands on the slot's left
     * edge, at "right" on its right edge, and at "center" the term is zero
     * either way. With no imposed slot — a standalone or Hug text — the box
     * IS the measured run and this is the run's centre.
     */
    getLayoutContentCenter(): { x: number; y: number } | null {
        const m = this._measureContentBox();
        return { x: m.layoutCenterX, y: m.centerY };
    }

    /**
     * Derive the layout/render parameters from resolved spec fields. Shared
     * by rendering (`updateTextContent`) and the editor layout
     * (`getEditLayout`) so the two consumers can't drift.
     */
    private _deriveParagraphParams(): {
        scale: number;
        anchorY: 'top' | 'middle' | 'bottom';
        rendererLineHeight: number;
        layoutParams: TextLayoutParams;
    } {
        const data = this._resolved;
        const fontSize = finiteOr(data.fontSize, 14);
        const lineHeight = finiteOr(data.lineHeight, 1.2);
        const letterSpacingEm = finiteOr(data.letterSpacing, 0);
        const anchorX = data.anchorX ?? 'center';
        const anchorY = data.anchorY ?? 'middle';

        const commonLineHeightFontUnits = this._getCommonLineHeightFontUnits();
        const rendererLineHeight = lineHeightEmToRendererMultiplier(
            lineHeight, this._metrics, commonLineHeightFontUnits,
        );

        return {
            scale: fontSizeToWorldScale(fontSize),
            anchorY,
            rendererLineHeight,
            layoutParams: {
                lineHeightUnits: commonLineHeightFontUnits * rendererLineHeight,
                letterSpacingUnits: letterSpacingToFontUnits(letterSpacingEm, this._metrics),
                // anchorX drives per-line alignment *and* the box anchor —
                // the same axis under our collapsed model.
                anchorXFraction: anchorXFraction(anchorX),
                // Same constraint the measurement used, converted through the
                // LOADED font's em-square: rendered breaks and measured breaks
                // are the same breaks (both faces are the same bundled BMFont,
                // so the two em-squares agree).
                maxWidthUnits: this._maxWidthUnits(
                    this._layoutWrapWidth, this._metrics.emSize, fontSizeToWorldScale(fontSize),
                ),
                wrapEpsilonUnits: this._wrapEpsilonUnits(
                    this._metrics.emSize, fontSizeToWorldScale(fontSize),
                ),
            },
        };
    }

    /** Adapt the loaded FontAsset's internals to the layout module's font API. */
    private _fontApi(): LayoutFontApi {
        const fontAsset = this.fontAsset as unknown as {
            _getChar(c: number): LayoutFontChar;
            _getKerning(a: number, b: number): number;
        };
        return {
            getChar: (c) => fontAsset._getChar(c),
            getKerning: (a, b) => fontAsset._getKerning(a, b),
        };
    }

    // ============================================================================
    // SELECTION MESH (invisible picker for edit-mode clicking)
    // ============================================================================

    /**
     * Fit the pick box to the paragraph's line box: the measurement layout
     * sizes and centres the node by (getLayoutContentSize /
     * getLayoutContentCenter) and the editor outlines, so a click lands where
     * the text is drawn whatever its anchors. The only writer of the box's
     * transform — the paragraph rebuild and attach both call it, so a load
     * and a live edit cannot place it differently.
     */
    private _placeSelectionMesh(): void {
        const parent = this._getParentNode();
        const box = this.textRenderer ? this._measureContentBox() : null;
        if (!box || box.width <= 0 || box.height <= 0 || !parent) {
            // Nothing drawn (or nowhere to parent) — keep the mesh but take it
            // out of the picking/rendering world until the text has bounds again.
            this._selectionMesh?.setEnabled(false);
            return;
        }

        // Create once as a unit box, then mutate scale/position per rebuild.
        // A persistent mesh keeps its identity across text edits, so editor
        // attachments (bounds gizmos, pick caches) don't churn or observe a
        // disposed mesh mid-frame.
        if (!this._selectionMesh || this._selectionMesh.isDisposed()) {
            this._selectionMesh = MeshBuilder.CreateBox(
                `${this.sceneObject.name}${SELECTION_MESH_SUFFIX}`,
                { size: 1 },
                this.sceneObject.babylonScene,
            );
            this._selectionMesh.visibility = 0;
            this._selectionMesh.isPickable = true;
        }
        // Re-assert the FULL local transform, not just size: the behavior
        // owns this mesh's transform, and external tooling (e.g. bounding
        // box gizmos computing bounds through a degenerate parent scale)
        // can perturb it in ways it cannot undo.
        this._selectionMesh.rotationQuaternion = null;
        this._selectionMesh.rotation.set(0, 0, 0);
        this._selectionMesh.scaling.set(box.width, box.height, box.height * 0.1);
        this._selectionMesh.position.set(box.centerX, box.centerY, 0);
        this._selectionMesh.parent = parent;
        this._selectionMesh.setEnabled(true);
    }

    private _disposeSelectionMesh(): void {
        if (this._selectionMesh) {
            this._selectionMesh.dispose();
            this._selectionMesh = null;
        }
    }

    /**
     * Wait (bounded) for a new face's atlas to finish loading. Babylon's
     * FontAsset starts the texture download in its constructor and nothing
     * awaits it, so a renderer installed at once draws blank glyphs until the
     * PNG lands. After ATLAS_WAIT_MS the face is installed anyway: its glyphs
     * appear when the texture does, and stay blank if it never does. Keeping
     * the old face instead would leave a slow connection on it for good. A
     * newer `load()` (which bumps the generation when it is queued) or a
     * detach ends the wait early (`isStale`).
     */
    private async _whenAtlasReady(asset: FontAsset, isStale: () => boolean): Promise<void> {
        const ready = (): boolean => asset.textures.every((texture) => texture.isReady());
        if (ready()) return;
        const deadline = performance.now() + ATLAS_WAIT_MS;
        while (!isStale() && !ready() && performance.now() < deadline) {
            await new Promise<void>((resolve) => setTimeout(resolve, 16));
        }
    }

    private disposeTextRenderer(): void {
        // Invalidate any in-flight createTextRenderer() so its products
        // are discarded rather than installed onto a disposed behavior.
        this._loadGeneration++;
        this._releaseRenderer();
    }

    /** Dispose the drawing renderer and what hangs off it, without cancelling loads. */
    private _releaseRenderer(): void {
        this._appliedParagraph = null;
        this._disposeSelectionMesh();
        unregisterTextCaptureSource(this.scene, this);

        if (this.renderObserver && this.scene) {
            this.scene.onAfterCameraRenderObservable.remove(this.renderObserver);
            this.renderObserver = null;
        }
        if (this.textRenderer) {
            this.textRenderer.dispose();
            this.textRenderer = null;
        }
        if (this.fontAsset) {
            this.fontAsset.dispose();
            this.fontAsset = null;
        }
    }

    private _getParentNode(): TransformNode | null {
        return this.sceneObject.animationRoot || this.attachedNode;
    }

    // ============================================================================
    // COMMIT & VALIDATION
    // ============================================================================

    protected async applyCommit(_patch: Partial<TextData>, _prev: TextData): Promise<void> {
        // Layout measures through the metrics registry, not the renderer, so
        // the new box is known now. Publish it before any reload, and whether
        // or not a renderer exists (a failed font fetch, headless): a font
        // that resolves after creation, or a style swap, has to reflow the
        // container rather than wait on an atlas that may never load. An
        // unchanged box publishes nothing (see _publishMeasurement).
        // If load() below rejects, the base rolls _resolved back and tells us
        // through onCommitRolledBack, which publishes again. load() only
        // rejects when the default face is missing from the build
        // (resolveAgainst); fetch and atlas failures are caught in
        // createTextRenderer.
        this._publishMeasurement();
        // The body, likewise, is laid out without the renderer.
        void this._refreshBody();

        const newKey = currentFontKey(this._resolved);
        const fontChanged = newKey !== this._loadedFontKey;

        if (fontChanged) {
            this._loadedFontKey = newKey;
            // load() → createTextRenderer() already parents the new renderer
            // and applies color + paragraph from the current resolved data.
            await this.load();
            return;
        }

        if (!this.textRenderer) {
            // Renderer never came up (e.g. font fetch failed) — nothing to
            // draw; the measurement above has already reached layout.
            // createTextRenderer reads current _resolved when a font change
            // retries the load.
            return;
        }

        // Color/stroke are cheap renderer properties — apply unconditionally
        // so they're never lost when a commit also triggers a rebuild.
        this.applyTextProperties();

        if (this._paragraphStateChanged()) {
            this._resetAndRebuild();
        }
    }

    protected override onCommitRolledBack(): void {
        // The box published above belongs to data the base has just discarded,
        // so layout is holding a size for text that no longer exists. Measure
        // the restored data and publish that.
        this._publishMeasurement();
        // The key moved ahead to the rejected data's face, which was never
        // installed (the old renderer still draws). Clearing it makes the next
        // commit reload instead of treating that face as current.
        this._loadedFontKey = undefined;
    }

    /** Diff resolved paragraph fields against what the renderer last applied. */
    private _paragraphStateChanged(): boolean {
        if (!this._appliedParagraph) return true;
        return PARAGRAPH_FIELDS.some(
            field => this._resolved[field] !== this._appliedParagraph![field],
        );
    }

    /**
     * Reset renderer paragraphs and re-add with current text.
     * Synchronous — no dispose/recreate, no flicker. Reaches into Babylon
     * MSDF internals because there is no public clear API.
     *
     * TODO(babylon-upstream): _charMatrices/_charUvs/_isDirty are private
     * TextRenderer fields (also poked in updateTextContent) — a Babylon
     * bump that renames them silently reintroduces the ghost/double-strike
     * bug. Ask upstream for a public clearParagraphs()/reset() API and
     * replace both call sites when it lands.
     */
    private _resetAndRebuild(): void {
        if (!this.textRenderer || !this.fontAsset) return;

        const renderer = this.textRenderer as unknown as {
            _charMatrices: unknown[];
            _charUvs: unknown[];
            _baseLine: number;
            _isDirty: boolean;
        };
        renderer._charMatrices.length = 0;
        renderer._charUvs.length = 0;
        renderer._baseLine = (this.fontAsset as unknown as {
            _font: { common: { lineHeight: number } };
            scale: number;
        })._font.common.lineHeight * this.fontAsset.scale;
        renderer._isDirty = true;

        this.updateTextContent();
    }

    protected validate(data: TextData): ValidationError[] {
        const errors: ValidationError[] = [];

        if (data.fontSize !== undefined && data.fontSize <= 0) {
            errors.push({ field: 'fontSize', message: 'Font size must be positive' });
        }
        if (data.strokeWidth !== undefined && (data.strokeWidth < 0 || data.strokeWidth > 0.05)) {
            errors.push({ field: 'strokeWidth', message: 'Stroke width must be between 0 and 0.05 (em)' });
        }
        if (data.lineHeight !== undefined && data.lineHeight <= 0) {
            errors.push({ field: 'lineHeight', message: 'Line height must be positive' });
        }
        // A bound value ("{opacity}") is a string here and resolves later; it is
        // clamped when read, so only an authored number is range-checked.
        if (typeof data.opacity === 'number' && !(data.opacity >= 0 && data.opacity <= 1)) {
            errors.push({ field: 'opacity', message: 'Opacity must be between 0 and 1' });
        }
        if (typeof data.depth === 'number' && !(data.depth >= 0 && data.depth <= 1)) {
            errors.push({ field: 'depth', message: 'Depth must be between 0 and 1 (em)' });
        }
        return errors;
    }

    // ============================================================================
    // PUBLIC API
    // ============================================================================

    public setText(text: string): void {
        const data = this.getData();
        data.text = text;
        void this.commit(data);
    }

    public getText(): string {
        return this._resolved.text ?? 'Text';
    }

    public getTextRenderer(): TextRenderer | null {
        return this.textRenderer;
    }

    /**
     * Draw the current glyphs with the given view/projection — the single
     * draw entry shared by the live per-camera observer and RTT capture
     * passes (`TextCaptureSource`), so both apply identical guards.
     *
     * Skips when there are no glyphs: Babylon's TextRenderer.render() draws
     * unconditionally, and the engine's drawArraysType falls back to a
     * *non-instanced* drawArrays when instanceCount is 0 — which paints one
     * stale quad using the last glyph's leftover buffer data ("empty text
     * keeps the last character"). Also skips while the subtree is disabled
     * (Pages / states): disabled meshes are filtered out of render lists,
     * and the glyphs must follow.
     */
    public renderGlyphs(viewMatrix: Matrix, projectionMatrix: Matrix): void {
        if (
            this.textRenderer &&
            this.textRenderer.characterCount > 0 &&
            this.sceneObject.transform.isEnabled()
        ) {
            this.textRenderer.render(viewMatrix, projectionMatrix);
        }
    }

    /**
     * True when a capture pass can draw this text right now — the MSDF
     * shader effect is compiled, or there is nothing to draw
     * (`TextCaptureSource`). One-shot captures poll this in their readiness
     * wait: `TextRenderer.render()` silently early-returns while its effect
     * is still compiling, so a single-pass RTT render taken too early would
     * ship a thumbnail with meshes but no text (the cold-start variant of
     * the missing-text bug).
     *
     * Reaches into a private field because TextRenderer exposes no
     * readiness API (same situation as the paragraph internals poked in
     * `_resetAndRebuild` — see the TODO(babylon-upstream) there). A rename
     * upstream degrades to `false` → the capture readiness wait times out
     * (bounded, logged) and proceeds, matching its partial-thumbnail
     * contract.
     *
     * Deliberately does NOT mirror renderGlyphs' `isEnabled()` guard.
     * The MSDF effect compiles from renderer CREATION
     * (`CreateTextRendererAsync` → `_setShaders` → `engine.createEffect`),
     * not from drawing, so a disabled node's effect still becomes ready —
     * this wait always terminates on the normal compile schedule. And a
     * posed subtree capture checks readiness BEFORE its pose applies, so a
     * disabled-but-about-to-be-enabled text node (state/keyframe reveal)
     * must still be waited on here; short-circuiting on `!isEnabled()`
     * would let a cold-start posed card ship without its text.
     */
    public isCaptureReady(): boolean {
        if (!this.textRenderer || this.textRenderer.characterCount === 0) return true;
        const effect = (this.textRenderer as unknown as {
            _drawWrapperBase?: { effect?: { isReady(): boolean } | null };
        })._drawWrapperBase?.effect;
        return effect ? effect.isReady() : false;
    }

    /**
     * Invisible picker mesh on the paragraph's line box. Editor
     * overlays use it to anchor bounds UI (e.g. inline text editing) to the
     * rendered text box. Persistent across paragraph rebuilds — resized in
     * place, so attachments keep a stable mesh identity — but disabled
     * while the text has no ink. Null until the first render, and disposed
     * with the renderer (re-query after font changes).
     */
    public getSelectionMesh(): Mesh | null {
        return this._selectionMesh;
    }

    /**
     * Node-local edit layout for the current (or overridden) text, used by
     * editors for caret placement, selection highlighting, and pointer
     * hit-testing. `textOverride` lets an editor compute caret geometry for
     * uncommitted input (e.g. its hidden input field's value) without
     * waiting for the commit round-trip. Positions are in the space of
     * `getTextRootNode()` — the same space the rendered glyphs occupy.
     * Null until a font has loaded.
     */
    public getEditLayout(textOverride?: string): TextEditLayout | null {
        if (!this.fontAsset) return null;

        // Pure query — must not touch renderer state. (A copy of the
        // updateTextContent reset block here once wiped the live glyph
        // buffers on every caret move, blanking the text mid-edit.)
        const text = textOverride ?? this._resolved.text ?? 'Text';
        const { scale, anchorY, layoutParams } = this._deriveParagraphParams();

        const layout = computeTextLayout(text, layoutParams, this._fontApi());
        // Anchor on the line box — must mirror updateTextContent exactly, or
        // carets drift from the rendered glyphs.
        const box = computeLineBoxExtents(layout, anchorY, this._metrics.capTop);
        const fontScale = 1 / this._metrics.emSize;
        const yWorldOffset = computeAnchorYOffset(
            anchorY,
            box.yTop * fontScale,
            box.yBot * fontScale,
            scale,
        );
        return toEditLayout(layout, fontScale * scale, yWorldOffset, this._metrics.emSize);
    }

    /**
     * The node the text renderer's glyph geometry is parented to
     * (`animationRoot` when present, else the attached node). Editors parent
     * caret/selection meshes here so they inherit the same world transform
     * as the glyphs. Null before attach.
     */
    public getTextRootNode(): TransformNode | null {
        return this._getParentNode();
    }
}

/** How long a swap waits for a new face's atlas before installing it anyway. */
const ATLAS_WAIT_MS = 10_000;

function currentFontKey(d: TextData): string {
    return `${d.fontFamily ?? DEFAULT_FAMILY}|${d.fontWeight ?? DEFAULT_WEIGHT}|${d.fontStyle ?? DEFAULT_STYLE}`;
}
